import React, { useCallback } from 'react';

import classNames from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import BootstrapDropdown from 'react-bootstrap/Dropdown';

import { Loader } from '@src/components/ui';

import '../shared/dropdown.scss';

const ALL_OPTION = 'all-options';

/**
 * GroupedDropdown Component
 *
 * A dropdown component that displays options grouped by categories.
 * Supports loading states, blank options, and custom selection handling.
 * Supports nested groups.
 *
 * @component
 * @param {Object[]} [groupedList=[]] - Array of groups with items or subgroups
 * @param {boolean} [isLoading=false] - Whether to show loading state
 * @param {boolean} [allowBlank=true] - Whether to show "All Options" item
 * @param {string} [defaultSelection='All Categories'] - Default display text
 * @param {string} [selectedOption] - Currently selected option ID
 * @param {function} [onChange] - Callback when selection changes
 * @param {string} [title] - Title to display when nothing selected
 * @param {string} [className] - Additional CSS classes for the dropdown container
 * @returns {React.ReactElement} - Grouped dropdown component
 *
 * @example
 * ```jsx
 * <GroupedDropdown
 *   groupedList={[
 *     {
 *       group: 'Category A',
 *       items: [
 *         { id: 'item1', title: 'Item One', type: 'category' }
 *       ]
 *     },
 *     {
 *       group: 'Category B',
 *       groups: [
 *         {
 *           group: 'Subcategory B1',
 *           items: [{ id: 'item2', title: 'Sub Item' }]
 *         }
 *       ]
 *     }
 *   ]}
 *   selectedOption="item2"
 *   onChange={(id, type) => console.log(id, type)}
 *   title="Select Option"
 * />
 * ```
 */
const GroupedDropdown = ({
    groupedList = [],
    isLoading = false,
    allowBlank = true,
    defaultSelection = 'All Categories',
    selectedOption,
    onChange,
    title,
    className,
}) => {
    const handleOptionClick = useCallback((event) => {
        const targetId = event.nativeEvent.target.id;
        const targetType = event.nativeEvent.target.type;

        if (onChange) {
            onChange(targetId, targetType);
        }
    }, [onChange]);

    /**
 * Find an option by ID in a nested grouped list
 */
    const findOption = (groups, id) => {
        for (const group of groups) {
            if (group.items) {
                const item = group.items.find((o) => o.id === id);
                if (item) {
                    return item;
                }
            }

            if (group.groups) {
                const item = findOption(group.groups, id);
                if (item) {
                    return item;
                }
            }
        }

        return null;
    };

    const selectedLabel = useCallback(() => {
        if (!selectedOption || selectedOption === ALL_OPTION) {
            return title;
        }

        if (!groupedList || groupedList.length === 0) {
            return title;
        }

        const thisSelectedOption = findOption(groupedList, selectedOption);
        return get(thisSelectedOption, 'title', title);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption, title, groupedList]);

    /**
     * Recursively render groups and items
     */
    function renderGroupContent(groups, level = 0) {
        if (!groups || groups.length === 0) {
            return null;
        }

        return groups.map((list) => {
            const headerClass = classNames({
                'dropdown-header': true,
                'is-subgroup': level > 0,
            });

            const itemClass = classNames({
                'is-subgroup-item': level > 0,
            });

            return (
                <React.Fragment key={`li-group-${list.group}`}>
                    <BootstrapDropdown.Header key={`li-header-${list.group}`} className={headerClass}>
                        <b>{list.group}</b>
                    </BootstrapDropdown.Header>
                    {
                        list.items && list.items.map((item) => (
                            <BootstrapDropdown.Item
                                key={`li-${item.id}`}
                                id={item.id}
                                type={item.type}
                                eventKey={item.id}
                                tabIndex={-1}
                                role="menuitem"
                                onClick={handleOptionClick}
                                className={itemClass}
                            >
                                {item.title}
                            </BootstrapDropdown.Item>
                        ))
                    }
                    {
                        list.groups && renderGroupContent(list.groups, level + 1)
                    }
                </React.Fragment>
            );
        });
    }

    const renderOpen = useCallback(() => {
        const selectOptionsClass = classNames({
            'select-options': true,
        });

        if (isLoading) {
            return (
                <BootstrapDropdown.Menu className={selectOptionsClass}>
                    <Loader />
                </BootstrapDropdown.Menu>
            );
        }

        return (
            <BootstrapDropdown.Menu
                key={`dropdown-select-ul-${title}`}
                id={`dropdown-select-ul-${title}`}
                className={selectOptionsClass}
                role="menu"
            >
                {
                    allowBlank
                    && (
                        <BootstrapDropdown.Item
                            key={`li-all-options-${title}`}
                            eventKey=""
                            tabIndex={-1}
                            role="menuitem"
                            onClick={handleOptionClick}
                        >
                            {defaultSelection}
                        </BootstrapDropdown.Item>
                    )
                }
                {renderGroupContent(groupedList)}
            </BootstrapDropdown.Menu>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, isLoading, allowBlank, defaultSelection, handleOptionClick, groupedList]);

    return (
        <div
            key={`dropdown-container-${title}`}
            className={classNames('dropdown-container ml-form floating-label', className)}
        >
            <div className="ml-form-field ml-form-field-select grouped">
                <div className="select">
                    <BootstrapDropdown
                        key={`dropdown-label-${title}`}
                        role="menu"
                    >
                        <BootstrapDropdown.Toggle variant="primary" className="select-styled">
                            {selectedLabel()}
                        </BootstrapDropdown.Toggle>
                        {renderOpen()}
                    </BootstrapDropdown>
                </div>
            </div>
        </div>
    );
};

// Recursive prop types for nested groups
const groupShape = {
    group: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.array,
        ]).isRequired,
        type: PropTypes.string,
    })),
};
groupShape.groups = PropTypes.arrayOf(PropTypes.shape(groupShape));

GroupedDropdown.propTypes = {
    groupedList: PropTypes.arrayOf(PropTypes.shape(groupShape)),
    isLoading: PropTypes.bool,
    allowBlank: PropTypes.bool,
    defaultSelection: PropTypes.string,
    selectedOption: PropTypes.string,
    onChange: PropTypes.func,
    title: PropTypes.string,
    className: PropTypes.string,
};

export default React.memo(GroupedDropdown);
export { ALL_OPTION }; // Export for backward compatibility

